const brandBase = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    borderWidth: '1.5px',
    borderColor: state.isFocused ? 'var(--primary)' : 'var(--input)',
    borderRadius: 8,
    backgroundColor: 'transparent',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--ring) 15%, transparent)' : 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    transition: 'all 200ms',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.5 : 1,
    '&:hover': {
      borderColor: 'var(--primary)',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--primary)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--primary) 8%, transparent)'
        : 'transparent',
    color: state.isSelected ? 'var(--primary-foreground)' : 'var(--foreground)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    cursor: 'pointer',
    borderRadius: 4,
    '&:active': {
      backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--popover)',
    borderRadius: 8,
    border: '1px solid var(--border)',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
    zIndex: 50,
    overflow: 'hidden',
  }),
  menuList: (base) => ({
    ...base,
    padding: 4,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--foreground)',
    fontFamily: 'var(--font-body)',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'var(--muted)',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--foreground)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--muted-foreground)',
    borderRadius: '0 6px 6px 0',
    '&:hover': {
      backgroundColor: 'var(--destructive)',
      color: 'var(--destructive-foreground)',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--muted-foreground)',
    fontFamily: 'var(--font-body)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--foreground)',
    fontFamily: 'var(--font-body)',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--border)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--muted-foreground)',
    padding: 6,
    '&:hover': {
      color: 'var(--foreground)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--muted-foreground)',
    padding: 6,
    '&:hover': {
      color: 'var(--destructive)',
    },
  }),
};

const mergeStyles = (overrides = {}) => {
  const merged = { ...brandBase };
  Object.keys(overrides).forEach((key) => {
    if (typeof overrides[key] === 'function' && typeof brandBase[key] === 'function') {
      merged[key] = (base, state) => ({
        ...brandBase[key](base, state),
        ...overrides[key](base, state),
      });
    } else {
      merged[key] = overrides[key];
    }
  });
  return merged;
};

const customStyle = mergeStyles({
  container: (base) => ({
    ...base,
    display: 'inline-block',
    float: 'right',
    width: '260px',
    marginTop: '40px',
  }),
});

const adminPortalSelect = mergeStyles({
  container: (base) => ({
    ...base,
    float: 'right',
    marginLeft: 40,
    paddingBottom: 10,
  }),
  control: (base, state) => ({
    width: 200,
  }),
});

/** Toolbars / card headers: wide, searchable (react-select types to filter), no float — e.g. admin org pickers. */
const adminToolbarSelect = mergeStyles({
  container: (base) => ({
    ...base,
    width: '100%',
    minWidth: 260,
    maxWidth: 420,
  }),
  control: (base) => ({
    ...base,
    minHeight: 36,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
  }),
});

/** Full-width selects in dense forms (Connections mapping rows) — no minWidth so grid cells don't collide. */
const mappingRowSelect = mergeStyles({
  container: (base) => ({
    ...base,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  }),
  control: (base) => ({
    ...base,
    minHeight: 36,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
  }),
});
/** Dialog form fields: fills the field column, menu portalled so it clears the dialog overflow. */
const modalSelect = mergeStyles({
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
  }),
});

const selectStyles = mergeStyles({
  container: (base) => ({
    ...base,
    display: 'inline-block',
    float: 'right',
    width: '30vw',
    minWidth: '30vw',
  }),
});

const flexBetweenCenter = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const whiteLabelSelect = mergeStyles({
  container: (base) => ({
    ...base,
    display: 'inline-block',
    float: 'right',
    width: '350px',
  }),
});

const paymentMethodSelectStyles = mergeStyles({
  container: (base) => ({
    ...base,
    width: '65%',
  }),
  menu: (base) => ({
    ...base,
    width: '200px',
  }),
});

const facilitySelect = mergeStyles({
  container: (base) => ({
    ...base,
    width: '165px',
    float: 'right',
    bottom: 7,
  }),
  control: (base, state) => ({
    height: 30,
    minHeight: 30,
  }),
});

const accountCodeSelectStyle = mergeStyles({
  container: (base) => ({
    ...base,
    display: 'inline-block',
    maxWidth: 1200,
    minWidth: 287.594,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
});

const xeroAccountCodeSelectStyle = mergeStyles({
  container: (base) => ({
    ...base,
    display: 'inline-block',
    width: 300,
  }),
  control: (base) => ({
    ...base,
    flexWrap: 'nowrap',
  }),
  valueContainer: (base) => ({
    ...base,
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  multiValue: (base) => ({
    ...base,
    minWidth: 0,
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
});

export {
  brandBase,
  mergeStyles,
  customStyle,
  adminPortalSelect,
  adminToolbarSelect,
  mappingRowSelect,
  modalSelect,
  selectStyles,
  flexBetweenCenter,
  whiteLabelSelect,
  paymentMethodSelectStyles,
  facilitySelect,
  accountCodeSelectStyle,
  xeroAccountCodeSelectStyle,
};
